/**
 * Date utilities for handling timezone-aware dates
 * 
 * Problem: Events are created in local time but some dates are stored with UTC offset.
 * When displayed, they need to show the intended local date, not UTC conversion.
 * 
 * Solution: 
 * - For dates with timezone info (Z or +00:00), format them in Argentina timezone
 * - For dates without timezone, treat them as local Argentina time
 */

import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';

// Argentina timezone
const ARGENTINA_TZ = 'America/Argentina/Buenos_Aires';

/**
 * Check if a date string has timezone information
 */
const hasTimezoneInfo = (dateString) => {
  if (!dateString) return false;
  // Check for Z, +XX:XX, or -XX:XX at the end
  return /[Z]$|[+-]\d{2}:\d{2}$/.test(dateString);
};

/**
 * Parse an event date string and return a Date object
 * that represents the intended local time for display.
 */
export const parseEventDate = (dateString) => {
  if (!dateString) return null;
  
  try {
    if (hasTimezoneInfo(dateString)) {
      // For UTC dates, we need to format in Argentina TZ
      // But parseEventDate should return a Date that when formatted locally
      // will show the correct Argentina time
      const utcDate = parseISO(dateString);
      // Get the Argentina time components
      const argTimeStr = formatInTimeZone(utcDate, ARGENTINA_TZ, "yyyy-MM-dd'T'HH:mm:ss");
      // Parse this as a local date (without timezone conversion)
      return parseISO(argTimeStr);
    }
    
    // No timezone info - treat as local time already
    return parseISO(dateString);
  } catch (error) {
    console.error('Error parsing date:', error);
    return new Date(dateString);
  }
};

/**
 * Format an event date for display
 * @param {string} dateString - ISO date string from backend
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted date string
 */
export const formatEventDate = (dateString, formatStr = "EEE d MMM · HH:mm") => {
  if (!dateString) return '';
  
  try {
    if (hasTimezoneInfo(dateString)) {
      // Format directly in Argentina timezone
      return formatInTimeZone(parseISO(dateString), ARGENTINA_TZ, formatStr, { locale: es });
    }
    
    // No timezone - parse and format as-is (local time)
    const date = parseISO(dateString);
    return format(date, formatStr, { locale: es });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format event date for full display (día de mes año)
 */
export const formatEventDateFull = (dateString) => {
  return formatEventDate(dateString, "d 'de' MMMM yyyy");
};

/**
 * Format event date with day of week, date and time
 */
export const formatEventDateTime = (dateString) => {
  return formatEventDate(dateString, "EEE d MMM · HH:mm");
};

/**
 * Get Date object for comparison (e.g., checking if event is in the past)
 */
export const getEventDateForComparison = (dateString) => {
  if (!dateString) return null;
  
  if (hasTimezoneInfo(dateString)) {
    // Use the actual UTC time for comparison
    return parseISO(dateString);
  }
  
  return parseISO(dateString);
};

/**
 * Check if event date is in the past (event has ended)
 * Considers the event finished 6 hours after the start time
 * @param {string} dateString - ISO date string from backend
 * @param {string} eventTime - Optional time string like "23:00" or "23:30"
 */
export const isEventPast = (dateString, eventTime = null) => {
  if (!dateString) return false;
  let eventDate = getEventDateForComparison(dateString);
  if (!eventDate) return false;
  
  // If event time is provided, combine it with the date
  // This ensures events at 23:00 aren't marked as past at 05:00 the same day
  if (eventTime && typeof eventTime === 'string') {
    const timeParts = eventTime.match(/(\d{1,2}):(\d{2})/);
    if (timeParts) {
      const hours = parseInt(timeParts[1], 10);
      const minutes = parseInt(timeParts[2], 10);
      // Create new date with the event's actual start time
      eventDate = new Date(eventDate);
      eventDate.setHours(hours, minutes, 0, 0);
    }
  }
  
  // Add 6 hours buffer - event is considered "past" 6 hours after start time
  const eventEndEstimate = new Date(eventDate.getTime() + (6 * 60 * 60 * 1000));
  return eventEndEstimate < new Date();
};

export default {
  parseEventDate,
  formatEventDate,
  formatEventDateFull,
  formatEventDateTime,
  getEventDateForComparison,
  isEventPast
};
